import {
	type BalanceSheetId,
	FileId,
	type FinancialPeriodPreparationMethod,
	type ISODate,
	type ISODateTime,
	type PeriodType,
	type SuretyAccountId,
} from "@inrev/common";
import { useFilePreview } from "@portal/api/files";
import {
	useAdminDeleteBalanceSheet,
	useAdminMarkBalanceSheetReviewed,
	useAdminMarkBalanceSheetUnreviewed,
	useAdminPromoteBalanceSheet,
	useAdminUpdateBalanceSheetPeriod,
} from "@portal/api/financials/admin";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { FC, useState } from "react";
import { useGetFileUrl } from "../../api";
import { PeriodDeleteModal } from "../ui/Financials/PeriodDeleteModal";
import { PeriodHeaderActionRow } from "../ui/Financials/PeriodHeaderActionRow";
import { PeriodReviewedModal } from "../ui/Financials/PeriodReviewedModal";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";
import { PDFViewerModal } from "../ui/PDFViewerModal";
import { CreateOrUpdatePeriodModal } from "./CreateOrUpdatePeriodModal";

export type BalanceSheetPeriodHeaderContextMenuProps = {
	accountId: SuretyAccountId;
	balanceSheetData: {
		id: BalanceSheetId;
		fileId?: FileId;
		reviewedAt?: ISODateTime;
		periodEndDate?: ISODate;
		periodType?: PeriodType;
		preparationMethod?: FinancialPeriodPreparationMethod;
	};
	isActive?: boolean;
};

export const BalanceSheetPeriodHeaderContextMenu: FC<BalanceSheetPeriodHeaderContextMenuProps> = ({
	accountId,
	balanceSheetData,
	isActive,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const [showReviewedModal, setShowReviewedModal] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const { pdfViewerControl, openFilePreview, closeFilePreview } = useFilePreview(
		balanceSheetData.fileId,
	);
	const { showErrorFlag } = useFlags();
	const { reviewBalanceSheet, reviewBSLoading, reviewBSError, resetReviewBSError } =
		useAdminMarkBalanceSheetReviewed(accountId);
	const { unreviewBalanceSheet, unreviewBSLoading, unreviewBSError, resetUnreviewBSError } =
		useAdminMarkBalanceSheetUnreviewed(accountId);
	const { promoteBalanceSheet, promoteBSLoading, promoteBSError, resetPromoteBSError } =
		useAdminPromoteBalanceSheet(accountId);
	const { deleteBalanceSheet, deleteBSError, resetDeleteBSError } =
		useAdminDeleteBalanceSheet(accountId);
	const {
		updateBalanceSheetPeriod,
		updateBSPeriodLoading,
		updateBSPeriodError,
		resetUpdateBSPeriodError,
	} = useAdminUpdateBalanceSheetPeriod(accountId);
	const { getFileUrl, fileUrlError, fileUrlLoading, resetFileUrlError } = useGetFileUrl();
	const isReviewed = !!balanceSheetData.reviewedAt;
	const hasFile = !!balanceSheetData.fileId;

	const handleDownload = () => {
		if (fileUrlLoading) return;
		if (!balanceSheetData.fileId) {
			showErrorFlag({ heading: "Download Error", message: "No file associated with this period." });
			return;
		}
		getFileUrl({
			baseUrl: `/v2/files/${balanceSheetData.fileId}`,
			queryKey: ["periodFile", balanceSheetData.fileId],
			asDownload: true,
		});
	};

	const handlePreview = () => {
		if (!balanceSheetData.fileId) {
			showErrorFlag({ heading: "Preview Error", message: "No file associated with this period." });
			return;
		}
		openFilePreview();
	};

	const handleReview = () => reviewBalanceSheet(balanceSheetData.id);
	const handleUnreview = () => unreviewBalanceSheet(balanceSheetData.id);

	return (
		<div
			data-testid="balance-sheet-period-header-context-menu"
			className="absolute top-8 left-0 bg-surface-base border border-bc-default rounded w-max"
		>
			<div className="flex flex-col">
				{hasFile && (
					<>
						<PeriodHeaderActionRow
							testId="download-bs-file-button"
							label="Download"
							iconType="arrow-down-tray"
							onClick={handleDownload}
							loading={fileUrlLoading}
						/>
						<PeriodHeaderActionRow
							testId="preview-bs-file-button"
							label="Preview"
							iconType="eye"
							onClick={handlePreview}
						/>
					</>
				)}
				{isAdmin && (
					<>
						{isReviewed ? (
							<PeriodHeaderActionRow
								testId="unreview-bs-button"
								label="Unmark Reviewed"
								iconType="document-text"
								onClick={() => setShowReviewedModal(true)}
								loading={unreviewBSLoading}
							/>
						) : (
							<PeriodHeaderActionRow
								testId="review-bs-button"
								label="Mark Reviewed"
								iconType="document-check"
								onClick={() => setShowReviewedModal(true)}
								loading={reviewBSLoading}
							/>
						)}
						{!isActive && (
							<PeriodHeaderActionRow
								testId="promote-bs-button"
								label="Promote"
								iconType="star"
								onClick={() => promoteBalanceSheet(balanceSheetData.id)}
								loading={promoteBSLoading}
							/>
						)}
						<PeriodHeaderActionRow
							testId="edit-bs-button"
							label="Edit"
							iconType="pencil-square"
							onClick={() => setShowEditModal(true)}
							loading={updateBSPeriodLoading}
						/>
						<PeriodHeaderActionRow
							testId="delete-bs-button"
							label="Delete"
							iconType="trash"
							onClick={() => setShowDeleteConfirmation(true)}
						/>
					</>
				)}
			</div>
			{showReviewedModal && (
				<PeriodReviewedModal
					reviewFn={isReviewed ? undefined : handleReview}
					unreviewFn={isReviewed ? handleUnreview : undefined}
					onClose={() => setShowReviewedModal(false)}
				/>
			)}
			{showDeleteConfirmation && (
				<PeriodDeleteModal
					deleteFn={() => deleteBalanceSheet(balanceSheetData.id)}
					onClose={() => setShowDeleteConfirmation(false)}
				/>
			)}
			{showEditModal && (
				<CreateOrUpdatePeriodModal
					accountId={accountId}
					onClose={() => setShowEditModal(false)}
					editConfig={{
						header: "Edit Balance Sheet Period",
						submitLabel: "Save",
						isLoading: updateBSPeriodLoading,
						error: updateBSPeriodError,
						dismissError: resetUpdateBSPeriodError,
						initialValues: {
							periodEndDate: balanceSheetData.periodEndDate,
							periodType: balanceSheetData.periodType,
							preparationMethod: balanceSheetData.preparationMethod,
						},
						submit: (body, { onSuccess }) =>
							updateBalanceSheetPeriod(
								{ balanceSheetId: balanceSheetData.id, body },
								{ onSuccess },
							),
					}}
				/>
			)}
			<PDFViewerModal
				control={pdfViewerControl}
				onClose={closeFilePreview}
				trackingId="balance-sheet-source-viewer"
				header={`Balance Sheet${balanceSheetData.periodEndDate ? ` - ${balanceSheetData.periodEndDate}` : ""}`}
			/>
			<MutationErrorFlag
				errorHeading="Mark Reviewed Error"
				mutationError={reviewBSError}
				dismissError={resetReviewBSError}
			/>
			<MutationErrorFlag
				errorHeading="Unmark Reviewed Error"
				mutationError={unreviewBSError}
				dismissError={resetUnreviewBSError}
			/>
			<MutationErrorFlag
				errorHeading="Download Error"
				mutationError={fileUrlError}
				dismissError={resetFileUrlError}
			/>
			<MutationErrorFlag
				errorHeading="Promote Error"
				mutationError={promoteBSError}
				dismissError={resetPromoteBSError}
			/>
			<MutationErrorFlag
				errorHeading="Delete Error"
				mutationError={deleteBSError}
				dismissError={resetDeleteBSError}
			/>
		</div>
	);
};
